/**
 * ============================================================================
 * AppShell — Shell unificado de la aplicación autenticada
 * ============================================================================
 * Envuelve el contenido principal con SidebarProvider + AppSidebar y, en
 * desktop con sidebar expandido, expone un divisor arrastrable que ajusta
 * `--sidebar-width` en vivo.
 *
 * Decisiones de diseño:
 *  - Coexistencia con shadcn Sidebar: mantenemos `<Sidebar collapsible="icon">`
 *    intacto. El resize SOLO opera en desktop + estado "expanded". En mobile
 *    o icon-mode, el divisor no se renderiza y todo cae al comportamiento
 *    nativo (Sheet en mobile, ancho fijo en icon-mode).
 *  - Persistencia: el ancho elegido se guarda en localStorage para que cada
 *    usuario conserve su layout entre sesiones.
 *  - Límites: 12vw (≈ 230px en 1920) ≤ width ≤ 20vw (≈ 384px). Defaults a
 *    240px para alinear con el SIDEBAR_WIDTH original (16rem).
 * ============================================================================
 */

import * as React from "react";
import { useEffect, useRef, useState, useCallback } from "react";
import { SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "kn:sidebar:width-px";
const DEFAULT_PX = 256; // 16rem
const MIN_VW = 12;
const MAX_VW = 20;

function clampWidthPx(px: number): number {
  if (typeof window === "undefined") return px;
  const min = (MIN_VW / 100) * window.innerWidth;
  const max = (MAX_VW / 100) * window.innerWidth;
  return Math.max(min, Math.min(max, px));
}

function readInitialWidth(): number {
  if (typeof window === "undefined") return DEFAULT_PX;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const n = raw ? Number(raw) : DEFAULT_PX;
  return clampWidthPx(Number.isFinite(n) ? n : DEFAULT_PX);
}

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const isMobile = useIsMobile();
  const [sidebarWidthPx, setSidebarWidthPx] = useState<number>(() => readInitialWidth());

  // Mantén el ancho dentro de los límites cuando se redimensiona la ventana.
  useEffect(() => {
    const onResize = () => setSidebarWidthPx((w) => clampWidthPx(w));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Persistencia ligera (debounced via rAF para evitar thrash).
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(Math.round(sidebarWidthPx)));
      } catch {
        // ignore quota / privacy mode
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [sidebarWidthPx]);

  return (
    <SidebarProvider
      style={{ ["--sidebar-width" as string]: `${sidebarWidthPx}px` }}
    >
      <ShellBody
        isMobile={isMobile}
        sidebarWidthPx={sidebarWidthPx}
        setSidebarWidthPx={setSidebarWidthPx}
      >
        {children}
      </ShellBody>
    </SidebarProvider>
  );
}

interface ShellBodyProps {
  isMobile: boolean;
  sidebarWidthPx: number;
  setSidebarWidthPx: (v: number | ((prev: number) => number)) => void;
  children: React.ReactNode;
}

function ShellBody({ isMobile, sidebarWidthPx, setSidebarWidthPx, children }: ShellBodyProps) {
  const { state } = useSidebar();
  const showResizer = !isMobile && state === "expanded";

  return (
    <div className="min-h-screen flex w-full bg-background">
      {!isMobile && <AppSidebar />}
      {showResizer && (
        <ResizeHandle
          currentPx={sidebarWidthPx}
          onChange={setSidebarWidthPx}
        />
      )}
      <div className="flex-1 flex flex-col min-w-0">{children}</div>
    </div>
  );
}

/* ─────────────── Resize handle ─────────────── */

interface ResizeHandleProps {
  currentPx: number;
  onChange: (v: number | ((prev: number) => number)) => void;
}

function ResizeHandle({ currentPx, onChange }: ResizeHandleProps) {
  const [dragging, setDragging] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(currentPx);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = currentPx;
      setDragging(true);
    },
    [currentPx],
  );

  useEffect(() => {
    if (!dragging) return;

    const onMove = (e: PointerEvent) => {
      const delta = e.clientX - startXRef.current;
      const next = clampWidthPx(startWidthRef.current + delta);
      onChange(next);
    };
    const onUp = () => setDragging(false);

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);

    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, onChange]);

  // Doble-clic restaura el default.
  const onDoubleClick = useCallback(() => {
    onChange(clampWidthPx(DEFAULT_PX));
  }, [onChange]);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Redimensionar barra lateral"
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      className={cn(
        "relative z-20 hidden md:flex shrink-0 w-1.5 cursor-col-resize",
        "bg-border/40 hover:bg-primary/50 active:bg-primary transition-colors",
        "items-center justify-center group",
        dragging && "bg-primary",
      )}
      title="Arrastra para ajustar · doble clic para restaurar"
    >
      {/* Indicador visual sutil al hover */}
      <span
        className={cn(
          "h-8 w-0.5 rounded-full bg-foreground/0 group-hover:bg-foreground/30 transition-colors",
          dragging && "bg-foreground/60",
        )}
      />
    </div>
  );
}
